import { asColorLike } from 'ol/colorlike';
import {
  defaultFillStyle,
  defaultLineCap,
  defaultLineDash,
  defaultLineDashOffset,
  defaultLineJoin,
  defaultLineWidth,
  defaultMiterLimit,
  defaultStrokeStyle
} from 'ol/render/canvas';
import { logger } from 'cadenza/utils/logging';
import { Oval } from 'cadenza/map/geom/oval';
import { HIT_TOLERANCE } from 'cadenza/map/map-constants';

export function ovalRenderer (style) {
  return ([ center, corner ], { context, feature }) => {
    if (!(feature.getGeometry() instanceof Oval)) {
      logger.error('trying to draw other geometry with oval renderer', arguments);
      return;
    }

    const ovalStyle = style || feature.getStyle();
    if (!ovalStyle) {
      logger.error('missing style in oval renderer', arguments);
      return;
    }

    const fillStyle = ovalStyle.getFill();
    const strokeStyle = ovalStyle.getStroke();

    if (fillStyle === undefined && strokeStyle === undefined) {
      logger.error('missing fill/stroke style in oval renderer', arguments);
      return;
    }

    const rx = corner[0] - center[0];
    const ry = Math.abs(corner[1] - center[1]);

    context.save();
    context.beginPath();
    context.ellipse(center[0], center[1], rx, ry, 0, 0, 2 * Math.PI);
    fill(context, fillStyle);
    stroke(context, strokeStyle);
    context.closePath();
    context.restore();
  };
}

function getFillStyleColor (context, fillStyle) {
  const hitDetectionContextSize = Math.round(HIT_TOLERANCE) * 2 + 1; // Taken from CanvasReplayGroup.prototype.forEachFeatureAtCoordinate
  const isRenderingForHitDetection = context.canvas.width === hitDetectionContextSize
    && context.canvas.height === hitDetectionContextSize;
  if (isRenderingForHitDetection) {
    // OpenLayers does use the result of a custom renderer (this) for hit detection of the drawn
    // object (oval here) as well.
    // It basically uses the renderer to draw the oval on a small canvas context which has the size
    // of the hit tolerance and which is centered at cursor position.
    // To also be able to select an oval by clicking on their fully transparent area we need to draw
    // an image without alpha in case of hit detection. Otherwise, if alpha is 0, the occupied area
    // is not filled and thus not handled as hit area of the oval.
    return 'rgba(0,0,0,1)';
  } else {
    return asColorLike(fillStyle.getColor() || defaultFillStyle);
  }
}

// inspired by CanvasReplay.prototype.setFillStrokeStyle
function fill (context, fillStyle) {
  if (!fillStyle) {
    return;
  }
  context.fillStyle = getFillStyleColor(context, fillStyle);
  context.fill();
}

// inspired by CanvasReplay.prototype.setFillStrokeStyle
function stroke (context, strokeStyle) {
  if (!strokeStyle) {
    return;
  }
  context.strokeStyle = asColorLike(strokeStyle.getColor() || defaultStrokeStyle);
  context.lineCap = strokeStyle.getLineCap() !== undefined ? strokeStyle.getLineCap() : defaultLineCap;
  context.lineJoin = strokeStyle.getLineJoin() !== undefined ? strokeStyle.getLineJoin() : defaultLineJoin;
  context.lineWidth = strokeStyle.getWidth() !== undefined ? strokeStyle.getWidth() : defaultLineWidth;
  context.miterLimit = strokeStyle.getMiterLimit() !== undefined ? strokeStyle.getMiterLimit() : defaultMiterLimit;
  if (context.setLineDash) {
    context.lineDashOffset = strokeStyle.getLineDashOffset() || defaultLineDashOffset;
    context.setLineDash(strokeStyle.getLineDash() ? strokeStyle.getLineDash().slice() : defaultLineDash);
  }
  context.stroke();
}
